'use client';

import {
  useCallback,
  useMemo,
  useRef,
  useEffect,
  memo,
  forwardRef,
  useImperativeHandle,
} from 'react';
import CodeMirror from '@uiw/react-codemirror';
import type { ReactCodeMirrorRef } from '@uiw/react-codemirror';
import {
  EditorView,
  placeholder as placeholderExtension,
  Decoration,
  ViewPlugin,
  keymap,
} from '@codemirror/view';

import type { DecorationSet, ViewUpdate } from '@codemirror/view';
import { EditorState, Compartment, RangeSetBuilder } from '@codemirror/state';
import { highlightSelectionMatches } from '@codemirror/search';
import type { Extension } from '@codemirror/state';
import { styled, token } from '@terra/ui-styled-system';
import {
  httpLanguageSupport,
  detectContentType,
  isValidHttpContent,
  isHexContent,
  standaloneLanguageExtension,
} from './lezer';
import {
  HTTP_COLORS_DARK,
  HTTP_COLORS_LIGHT,
  HTTP_EDITOR_DARK_BACKGROUND,
  resolveTerraEditorTheme,
} from './terra-theme';
import { statusCodeDecorator } from './status-code-decorator';
import type { StatusCodeColors } from './status-code-decorator';
import { headerValueDecorator } from './header-value-decorator';
import type { HeaderValueColors } from './header-value-decorator';
import type {
  HttpEditorCodeMirrorProps,
  HttpEditorCodeMirrorHandle,
  HttpBodyLanguage,
  SearchOptions,
} from './types';
// =============================================================================
// Constants
// =============================================================================

/** Debounce delay for content analysis to avoid expensive parsing on every keystroke */
const CONTENT_ANALYSIS_DEBOUNCE_MS = 150;

/** Maximum search results to prevent performance degradation with large documents */
const MAX_SEARCH_RESULTS = 10000;

/** Match range for search highlighting */
type MatchRange = Readonly<{ from: number; to: number }>;

// =============================================================================
// Styled Components
// =============================================================================

const EditorContainer = styled('div', {
  base: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
    // Editor wrapper - @uiw/react-codemirror creates a wrapper div
    '& > div': {
      flex: 1,
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
    },
    // CodeMirror editor root
    '& .cm-editor': {
      flex: 1,
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
      outline: 'none',
    },
    // Scroller - the scrollable container
    '& .cm-scroller': {
      fontFamily: token('fonts.mono'),
      fontSize: '12px',
      lineHeight: '1.6',
      scrollbarWidth: 'none',
      scrollbarColor: 'var(--colors-gray-300) var(--colors-gray-100)',
      _hover: {
        scrollbarWidth: 'thin',
      },
    },
    // Remove unwanted underlines from syntax highlighting spans,
    // but allow decorator inline styles (which use [style]) to keep theirs
    '& .cm-line span:not([style])': {
      textDecoration: 'none !important',
    },
    // Focused state - no extra border
    '& .cm-editor.cm-focused': {
      outline: 'none',
    },
    // Content area
    '& .cm-content': {
      caretColor: 'currentColor',
    },
  },
  variants: {
    transparentBackground: {
      true: {
        '& > div, & .cm-editor, & .cm-scroller, & .cm-content, & .cm-gutters': {
          backgroundColor: 'transparent !important',
        },
      },
      false: {
        '&[data-theme="dark"], &[data-theme="dark"] > div, &[data-theme="dark"] .cm-editor, &[data-theme="dark"] .cm-scroller, &[data-theme="dark"] .cm-content, &[data-theme="dark"] .cm-gutters':
          {
            backgroundColor: `${HTTP_EDITOR_DARK_BACKGROUND} !important`,
          },
      },
    },
  },
  defaultVariants: {
    transparentBackground: false,
  },
});

const transparentEditorTheme = EditorView.theme({
  '&': {
    backgroundColor: 'transparent',
  },
  '.cm-gutters': {
    backgroundColor: 'transparent',
    border: 'none',
  },
  '.cm-activeLine, .cm-activeLineGutter': {
    backgroundColor: 'transparent',
  },
});

// =============================================================================
// Utility Hooks
// =============================================================================

/**
 * Provides a stable callback reference that always invokes the latest version.
 * Essential for CodeMirror extensions that capture callbacks in closures.
 *
 * @param callback - The callback function to stabilize
 * @returns A stable reference that always calls the current callback
 */
function useStableCallback<TArgs extends unknown[], TReturn>(
  callback: ((...args: TArgs) => TReturn) | undefined,
): (...args: TArgs) => TReturn | undefined {
  const callbackRef = useRef(callback);

  // Update ref on every render to capture latest callback
  useEffect(() => {
    callbackRef.current = callback;
  });

  return useCallback((...args: TArgs) => callbackRef.current?.(...args), []);
}

// =============================================================================
// Search Utilities
// =============================================================================

// Singleton decorations for performance - created once, reused everywhere
const SEARCH_MATCH_DECORATION = Decoration.mark({ class: 'cm-searchMatch' });
const SEARCH_CURRENT_MATCH_DECORATION = Decoration.mark({
  class: 'cm-searchMatch cm-searchMatch-selected',
});

/**
 * Escapes special regex characters in a string for literal matching.
 */
function escapeRegexPattern(pattern: string): string {
  return pattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Finds all search matches in a document.
 * Limited to MAX_SEARCH_RESULTS to prevent performance issues with large documents.
 *
 * @param doc - The document text to search
 * @param searchTerm - The search term (plain text or regex)
 * @param options - Search options (regex mode, case sensitivity)
 * @returns Array of match ranges, empty array if no matches or invalid pattern
 */
function findAllMatches(
  doc: string,
  searchTerm: string,
  options: SearchOptions,
): readonly MatchRange[] {
  // Early exit for empty inputs
  if (!searchTerm || !doc) {
    return [];
  }

  const matches: MatchRange[] = [];

  try {
    const flags = options.isCaseSensitive ? 'g' : 'gi';
    const pattern = options.isRegex
      ? searchTerm
      : escapeRegexPattern(searchTerm);

    const regex = new RegExp(pattern, flags);

    let match: RegExpExecArray | null;

    while ((match = regex.exec(doc)) !== null) {
      // Prevent infinite loop on zero-length matches (e.g., /(?:)/ or /a*/)
      if (match[0].length === 0) {
        regex.lastIndex++;
        continue;
      }

      matches.push({
        from: match.index,
        to: match.index + match[0].length,
      });

      // Limit matches to prevent performance issues
      if (matches.length >= MAX_SEARCH_RESULTS) {
        break;
      }
    }
  } catch {
    // Invalid regex pattern - return empty array silently
    // This is expected behavior when user is typing a regex
    return [];
  }

  return matches;
}

/**
 * Builds CodeMirror decorations for search matches.
 *
 * @param matches - Array of match ranges
 * @param currentIndex - Index of the current match to highlight differently
 * @returns DecorationSet for CodeMirror
 */
function buildSearchDecorations(
  matches: readonly MatchRange[],
  currentIndex: number,
): DecorationSet {
  const builder = new RangeSetBuilder<Decoration>();

  for (let i = 0; i < matches.length; i++) {
    const match = matches[i];
    const decoration =
      i === currentIndex
        ? SEARCH_CURRENT_MATCH_DECORATION
        : SEARCH_MATCH_DECORATION;

    builder.add(match.from, match.to, decoration);
  }

  return builder.finish();
}

function scrollEditorToMatch(view: EditorView, match: MatchRange): void {
  view.dispatch({
    selection: { anchor: match.from, head: match.to },
    effects: EditorView.scrollIntoView(match.from, { y: 'center' }),
  });
}

// =============================================================================
// CodeMirror Extension Factories
// =============================================================================

/**
 * Creates a compartment configuration object.
 * Compartments allow dynamic reconfiguration of extensions.
 */
function createCompartments() {
  return {
    language: new Compartment(),
    readOnly: new Compartment(),
    lineWrapping: new Compartment(),
    theme: new Compartment(),
    searchHighlight: new Compartment(),
    decorators: new Compartment(),
  } as const;
}

function getStatusCodeColors(isDark: boolean): StatusCodeColors {
  const c = isDark ? HTTP_COLORS_DARK : HTTP_COLORS_LIGHT;

  return {
    info: c.statusCode,
    success: c.statusSuccess,
    redirect: c.statusRedirect,
    clientError: c.statusClientError,
    serverError: c.statusServerError,
  };
}

function getHeaderValueColors(isDark: boolean): HeaderValueColors {
  const c = isDark ? HTTP_COLORS_DARK : HTTP_COLORS_LIGHT;

  return {
    base: c.headerValue,
    number: c.number,
    quoted: c.cookieValue,
    url: c.url,
    mime: c.method,
    directive: c.boolean,
    cookieKey: c.cookieKey,
    separator: c.punctuation,
    authType: c.authType,
    authToken: c.authToken,
  };
}

function createDecoratorExtensions(isDark: boolean) {
  return [
    statusCodeDecorator(getStatusCodeColors(isDark)),
    headerValueDecorator(getHeaderValueColors(isDark)),
  ];
}

type Compartments = ReturnType<typeof createCompartments>;

// =============================================================================
// Main Component
// =============================================================================

const HttpEditorCodeMirrorInner = forwardRef<
  HttpEditorCodeMirrorHandle,
  HttpEditorCodeMirrorProps
>(function HttpEditorCodeMirrorInner(props, ref) {
  const {
    value,
    onChange,
    readOnly = false,
    height = '100%',
    minHeight,
    maxHeight,
    placeholder,
    showLineNumbers = false,
    wordWrap = true,
    theme = 'light',
    onCursorChange,
    onSelectionChange,
    searchTerm,
    searchOptions,
    currentMatchIndex = 0,
    onMatchCountChange,
    onToggleSearch,
    languageOverride,
    transparentBackground = false,
    syntaxPalette = 'http',
  } = props;

  // ==========================================================================
  // Refs
  // ==========================================================================

  const editorRef = useRef<ReactCodeMirrorRef>(null);
  const lastContentTypeRef = useRef<HttpBodyLanguage>('text');
  const matchesRef = useRef<readonly MatchRange[]>([]);
  const currentMatchIndexRef = useRef(currentMatchIndex);

  currentMatchIndexRef.current = currentMatchIndex;

  // Store initial values for extensions setup (prevents recreation)
  const initialConfigRef = useRef({
    value,
    readOnly,
    wordWrap,
    theme,
    syntaxPalette,
  });

  // Compartments for dynamic reconfiguration - created once per component instance
  const compartmentsRef = useRef<Compartments | null>(null);

  if (compartmentsRef.current === null) {
    compartmentsRef.current = createCompartments();
  }

  const compartments = compartmentsRef.current;

  // ==========================================================================
  // Stable Callbacks
  // ==========================================================================

  const stableOnChange = useStableCallback(onChange);
  const stableOnCursorChange = useStableCallback(onCursorChange);
  const stableOnSelectionChange = useStableCallback(onSelectionChange);
  const stableOnMatchCountChange = useStableCallback(onMatchCountChange);
  const stableOnToggleSearch = useStableCallback(onToggleSearch);
  const hasToggleSearchRef = useRef(Boolean(onToggleSearch));

  useEffect(() => {
    hasToggleSearchRef.current = Boolean(onToggleSearch);
  });

  const scrollToCurrentMatch = useCallback(() => {
    const view = editorRef.current?.view;

    if (!view || !searchTerm) return;

    const matches = matchesRef.current;
    const matchIndex = currentMatchIndexRef.current;

    if (
      matches.length === 0 ||
      matchIndex < 0 ||
      matchIndex >= matches.length
    ) {
      return;
    }

    scrollEditorToMatch(view, matches[matchIndex]);
  }, [searchTerm]);

  // ==========================================================================
  // Imperative Handle
  // ==========================================================================

  useImperativeHandle(
    ref,
    () => ({
      goToLine: (line: number) => {
        const view = editorRef.current?.view;

        if (!view) return;

        const doc = view.state.doc;
        const targetLine = Math.min(Math.max(1, line), doc.lines);
        const lineInfo = doc.line(targetLine);

        view.dispatch({
          selection: { anchor: lineInfo.from },
          scrollIntoView: true,
        });
        view.focus();
      },

      getSelection: () => {
        const view = editorRef.current?.view;

        if (!view) return '';

        const { from, to } = view.state.selection.main;

        return view.state.sliceDoc(from, to);
      },

      getSelectionRange: () => {
        const view = editorRef.current?.view;

        if (!view) return null;

        const { from, to } = view.state.selection.main;

        if (from === to) return null;

        return {
          from,
          to,
          text: view.state.sliceDoc(from, to),
        };
      },

      replaceRange: (from: number, to: number, text: string) => {
        const view = editorRef.current?.view;

        if (!view || view.state.readOnly) return false;

        const docLength = view.state.doc.length;
        const safeFrom = Math.max(0, Math.min(from, docLength));
        const safeTo = Math.max(safeFrom, Math.min(to, docLength));

        view.dispatch({
          changes: { from: safeFrom, to: safeTo, insert: text },
          selection: { anchor: safeFrom + text.length },
        });
        view.focus();

        return true;
      },

      focus: () => {
        editorRef.current?.view?.focus();
      },

      scrollToCurrentMatch,
    }),
    [scrollToCurrentMatch],
  );

  // ==========================================================================
  // Selection/Cursor Update Extension
  // ==========================================================================

  const selectionUpdateExtension = useMemo(() => {
    return EditorView.updateListener.of((update: ViewUpdate) => {
      if (!update.selectionSet && !update.docChanged) {
        return;
      }

      const state = update.state;
      const selection = state.selection.main;

      // Calculate cursor position (1-based)
      const pos = selection.head;
      const line = state.doc.lineAt(pos);

      stableOnCursorChange({ line: line.number, column: pos - line.from + 1 });

      // Calculate selection info
      const selectedText = state.sliceDoc(selection.from, selection.to);
      const lineStart = state.doc.lineAt(selection.from).number;
      const endLineInfo = state.doc.lineAt(selection.to);
      const lineEnd =
        selection.from < selection.to && selection.to === endLineInfo.from
          ? Math.max(lineStart, endLineInfo.number - 1)
          : endLineInfo.number;
      const coords = update.view.coordsAtPos(selection.to) ?? undefined;

      stableOnSelectionChange({
        text: selectedText,
        length: selection.to - selection.from,
        lineStart,
        lineEnd,
        coords: coords ? { left: coords.left, top: coords.top } : undefined,
      });
    });
  }, [stableOnCursorChange, stableOnSelectionChange]);

  const shortcutKeymapExtension = useMemo(() => {
    return keymap.of([
      {
        key: 'Mod-f',
        run: () => {
          if (!hasToggleSearchRef.current) {
            return false;
          }

          stableOnToggleSearch();

          return true;
        },
      },
    ]);
  }, [stableOnToggleSearch]);

  // ==========================================================================
  // Search Highlight Extension Factory
  // ==========================================================================

  const createSearchHighlightExtension = useCallback(
    (
      term: string | undefined,
      options: SearchOptions | undefined,
      highlightIndex: number,
    ): Extension => {
      if (!term) {
        matchesRef.current = [];
        stableOnMatchCountChange(0);

        return [];
      }

      const searchTermValue = term;
      const searchOpts: SearchOptions = options ?? {};
      const currentIdx = highlightIndex;
      const onCount = stableOnMatchCountChange;

      return ViewPlugin.fromClass(
        class SearchHighlightPlugin {
          decorations: DecorationSet;

          constructor(view: EditorView) {
            const doc = view.state.doc.toString();
            const matches = findAllMatches(doc, searchTermValue, searchOpts);

            matchesRef.current = matches;
            this.decorations = buildSearchDecorations(matches, currentIdx);
            onCount(matches.length);
          }

          update(update: ViewUpdate) {
            if (update.docChanged) {
              const doc = update.state.doc.toString();
              const matches = findAllMatches(doc, searchTermValue, searchOpts);

              matchesRef.current = matches;
              this.decorations = buildSearchDecorations(matches, currentIdx);
              onCount(matches.length);
            }
          }
        },
        {
          decorations: (plugin) => plugin.decorations,
        },
      );
    },
    [stableOnMatchCountChange],
  );

  // ==========================================================================
  // Content Analysis (Debounced) — runs entirely via effect + ref to avoid
  // triggering a React re-render when the content type hasn't changed.
  // ==========================================================================

  useEffect(() => {
    if (languageOverride) return;

    const timer = setTimeout(() => {
      const view = editorRef.current?.view;

      if (!view) return;

      const doc = view.state.doc.toString();
      const isHttpValid = isValidHttpContent(doc);
      const isHex = isHexContent(doc);
      const detectedLanguage = isHttpValid ? detectContentType(doc) : 'text';

      if (detectedLanguage !== lastContentTypeRef.current) {
        lastContentTypeRef.current = detectedLanguage;

        const languageExtension =
          !isHttpValid || isHex ? [] : httpLanguageSupport(doc);

        view.dispatch({
          effects: compartments.language.reconfigure(languageExtension),
        });
      }
    }, CONTENT_ANALYSIS_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [value, languageOverride, compartments.language]);

  // Handle language override changes immediately
  useEffect(() => {
    if (!languageOverride) return;
    const view = editorRef.current?.view;

    if (!view) return;

    if (languageOverride !== lastContentTypeRef.current) {
      lastContentTypeRef.current = languageOverride;
      const ext = standaloneLanguageExtension(languageOverride) ?? [];

      view.dispatch({
        effects: compartments.language.reconfigure(ext),
      });
    }
  }, [languageOverride, compartments.language]);

  // Update read-only state
  useEffect(() => {
    const view = editorRef.current?.view;

    if (!view) return;

    view.dispatch({
      effects: compartments.readOnly.reconfigure([
        EditorState.readOnly.of(readOnly),
      ]),
    });
  }, [readOnly, compartments.readOnly]);

  // Update word wrap
  useEffect(() => {
    const view = editorRef.current?.view;

    if (!view) return;

    view.dispatch({
      effects: compartments.lineWrapping.reconfigure(
        wordWrap ? EditorView.lineWrapping : [],
      ),
    });
  }, [wordWrap, compartments.lineWrapping]);

  // Update theme and decorator colors together
  useEffect(() => {
    const view = editorRef.current?.view;

    if (!view) return;

    const isDark = theme === 'dark';
    const themeExtension = resolveTerraEditorTheme({ theme, syntaxPalette });

    view.dispatch({
      effects: [
        compartments.theme.reconfigure(themeExtension),
        compartments.decorators.reconfigure(createDecoratorExtensions(isDark)),
      ],
    });
  }, [theme, syntaxPalette, compartments.theme, compartments.decorators]);

  // Update search highlighting (extension plugin updates matchesRef on doc change)
  useEffect(() => {
    const view = editorRef.current?.view;

    if (!view) return;

    const searchExt = createSearchHighlightExtension(
      searchTerm,
      searchOptions,
      currentMatchIndex,
    );

    view.dispatch({
      effects: compartments.searchHighlight.reconfigure(searchExt),
    });
  }, [
    searchTerm,
    searchOptions,
    currentMatchIndex,
    createSearchHighlightExtension,
    compartments.searchHighlight,
  ]);

  useEffect(() => {
    scrollToCurrentMatch();
  }, [currentMatchIndex, searchTerm, scrollToCurrentMatch]);

  // ==========================================================================
  // Initial Extensions Setup
  // ==========================================================================

  const initialLanguage = useMemo(() => {
    if (languageOverride) {
      return standaloneLanguageExtension(languageOverride) ?? [];
    }

    const initialValue = initialConfigRef.current.value;

    if (!isValidHttpContent(initialValue) || isHexContent(initialValue)) {
      return [];
    }

    return httpLanguageSupport(initialValue);
  }, [languageOverride]);

  const extensions = useMemo(() => {
    const {
      readOnly: initialReadOnly,
      wordWrap: initialWordWrap,
      theme: initialTheme,
      syntaxPalette: initialSyntaxPalette,
    } = initialConfigRef.current;

    const initialThemeExt = resolveTerraEditorTheme({
      theme: initialTheme,
      syntaxPalette: initialSyntaxPalette,
    });

    const initialIsDark = initialTheme === 'dark';

    const baseExtensions: Extension[] = [
      compartments.theme.of(initialThemeExt),
      compartments.language.of(initialLanguage),
      compartments.readOnly.of([EditorState.readOnly.of(initialReadOnly)]),
      compartments.lineWrapping.of(
        initialWordWrap ? EditorView.lineWrapping : [],
      ),
      compartments.searchHighlight.of([]),
      compartments.decorators.of(createDecoratorExtensions(initialIsDark)),
      selectionUpdateExtension,
      shortcutKeymapExtension,
      highlightSelectionMatches(),
    ];

    if (transparentBackground) {
      baseExtensions.push(transparentEditorTheme);
    }

    if (placeholder) {
      baseExtensions.push(placeholderExtension(placeholder));
    }

    return [...baseExtensions];
  }, [
    initialLanguage,
    placeholder,
    selectionUpdateExtension,
    shortcutKeymapExtension,
    compartments,
    transparentBackground,
  ]);

  // ==========================================================================
  // Stable basicSetup config — prevents full extension reconfiguration on every render
  // ==========================================================================

  const basicSetupConfig = useMemo(
    () => ({
      lineNumbers: showLineNumbers,
      foldGutter: false,
      dropCursor: true,
      allowMultipleSelections: true,
      indentOnInput: true,
      bracketMatching: true,
      closeBrackets: true,
      autocompletion: false,
      rectangularSelection: true,
      crosshairCursor: false,
      highlightActiveLine: false,
      highlightSelectionMatches: false,
      closeBracketsKeymap: true,
      defaultKeymap: true,
      searchKeymap: false,
      historyKeymap: true,
      foldKeymap: false,
      completionKeymap: false,
      lintKeymap: false,
      tabSize: 2,
    }),
    [showLineNumbers],
  );

  // ==========================================================================
  // Computed Styles
  // ==========================================================================

  const heightValue = typeof height === 'number' ? `${height}px` : height;
  const minHeightValue =
    typeof minHeight === 'number' ? `${minHeight}px` : minHeight;
  const maxHeightValue =
    typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight;

  const containerStyle = useMemo(
    () => ({
      height: heightValue,
      minHeight: minHeightValue,
      maxHeight: maxHeightValue,
    }),
    [heightValue, minHeightValue, maxHeightValue],
  );

  // ==========================================================================
  // Render
  // ==========================================================================

  return (
    <EditorContainer
      style={containerStyle}
      data-theme={theme}
      transparentBackground={transparentBackground}
    >
      <CodeMirror
        ref={editorRef}
        value={value}
        onChange={stableOnChange}
        extensions={extensions}
        theme="none"
        basicSetup={basicSetupConfig}
        height={heightValue}
        minHeight={minHeightValue}
        maxHeight={maxHeightValue}
      />
    </EditorContainer>
  );
});

// =============================================================================
// Exports
// =============================================================================

/**
 * HTTP Editor component based on CodeMirror.
 *
 * Features:
 * - Syntax highlighting for HTTP requests/responses with Lezer grammar
 * - Nested language support for body content (JSON, HTML, XML, etc.)
 * - Search highlighting with regex support
 * - Cursor and selection tracking
 * - Dynamic theme switching (light/dark)
 * - Imperative API for programmatic control
 *
 * This is the core editor component used inside HttpEditor.
 * For the full-featured editor with toolbars and actions, use HttpEditor.
 */
export const HttpEditorCodeMirror = memo(HttpEditorCodeMirrorInner);

// Add display name for React DevTools
HttpEditorCodeMirror.displayName = 'HttpEditorCodeMirror';

export type { HttpEditorCodeMirrorProps, HttpEditorCodeMirrorHandle };
